import { useCallback, useEffect, useState } from "react";
import {
  Button, Confirm, ErrorNote, Field, Panel,
} from "../../components/ui";
import { formatMoney } from "../../lib/money";
import { cardPaymentsConfigured } from "../../lib/stripe";
import { openForPrint } from "../deals/buildPacket";
import ChargeCard from "./ChargeCard";
import {
  getCharge, chargeHistory, sendPaymentLink, cancelPaymentLink,
  voidCharge, refund, buildReceipt, archiveReceipt, mailReceipt,
  labelForEvent, STATUS_LABEL,
  type ChargeRow, type ChargeEvent,
} from "./charges";
import { describeSaveError, isOffline, toastSaved } from "../../lib/saving";

const STATUS_CLASS: Record<string, string> = {
  open: "hold", paid: "available", refunded: "recon", voided: "sold",
};

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  }) : "—";

/**
 * One payment, and everything that can be done to it.
 *
 * The three ways money arrives here are the card at the desk, a link the
 * customer opens, and — for an unpaid charge that is never going to be paid —
 * cancelling it. Once it is paid the only things left are the receipt and,
 * if it has to go back, a refund.
 */
export default function ChargeDetail({ id, onBack }: {
  id: string;
  onBack: () => void;
}) {
  const [charge, setCharge] = useState<ChargeRow | null>(null);
  const [events, setEvents] = useState<ChargeEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [taking, setTaking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkTo, setLinkTo] = useState("");
  const [sentUrl, setSentUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<"void" | "refund" | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([getCharge(id), chargeHistory(id)]);
      setCharge(c);
      setEvents(h);
      setLinkTo((t) => t || c.link_to_email || c.customer_email || "");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load the payment.");
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  /* The customer may be paying by link while this screen is open. Polling is
     the honest answer: there is no socket, and a page that quietly shows
     "Unpaid" after the money arrived is how the signing flow went wrong in
     October. Only while there is something to wait for. */
  const waiting = charge?.status === "open" && charge.has_link;
  useEffect(() => {
    if (!waiting) return;
    const tick = () => { void load(); };
    const timer = window.setInterval(tick, 20_000);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", tick); };
  }, [waiting, load]);

  /* What each action says once the server has confirmed it. */
  const DONE: Record<string, string> = {
    void: "Payment cancelled", refund: "Refunded", unlink: "Link stopped",
  };

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label); setErr(null); setNote(null);
    try {
      // Printing builds the PDF here; everything else needs the server.
      if (label !== "print" && isOffline()) throw new Error("offline");
      await fn();
      setReason("");
      if (DONE[label]) toastSaved(DONE[label]!);
      await load();
    } catch (e) {
      // Plain words for a dropped connection rather than "Failed to fetch".
      setErr(describeSaveError(e));
    } finally {
      setBusy(null);
    }
  }

  async function printReceipt() {
    if (!charge) return;
    await run("print", async () => {
      const bytes = await buildReceipt(charge);
      openForPrint(bytes, `${charge.charge_no}-receipt.pdf`);
      /* Printed is the part the person asked for; filing is ours. A filing
         failure is worth saying and not worth throwing away the print over. */
      try {
        await archiveReceipt(bytes, charge);
      } catch (e) {
        setNote(e instanceof Error ? e.message : "Printed, but not filed.");
      }
    });
  }

  async function emailReceipt() {
    if (!charge) return;
    const to = linkTo.trim() || charge.customer_email || "";
    if (!to) { setErr("No email address for this customer."); return; }
    await run("email", async () => {
      const bytes = await buildReceipt(charge);
      await mailReceipt(charge, bytes, to);
      setNote(`Receipt emailed to ${to}.`);
    });
  }

  if (err && !charge) return <ErrorNote>{err}</ErrorNote>;
  if (!charge) return <p className="hint">Loading</p>;

  const open = charge.status === "open";
  const paid = charge.status === "paid" || charge.status === "refunded";

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>{formatMoney(charge.amount_cents)}</h2>
          <p className="pagehead-sub">
            <span className="mono">{charge.charge_no}</span>
            {" · "}{charge.customer_name}
            {charge.description ? ` · ${charge.description}` : ""}
          </p>
        </div>
        <span className={`pill ${STATUS_CLASS[charge.status] ?? "hold"}`}>
          {STATUS_LABEL[charge.status]}
        </span>
      </div>

      {err && <ErrorNote>{err}</ErrorNote>}
      {note && <p className="hint">{note}</p>}

      <Panel title="Payment" rail="money"
             figure={formatMoney(charge.amount_cents)}>
        <dl className="spec">
          <div><dt>From</dt><dd>{charge.customer_name}</dd></div>
          {charge.customer_email && (
            <div><dt>Email</dt><dd>{charge.customer_email}</dd></div>)}
          {charge.description && (
            <div><dt>For</dt><dd>{charge.description}</dd></div>)}
          {charge.vehicle && (
            <div><dt>Vehicle</dt><dd>{charge.vehicle}</dd></div>)}
          {charge.vin && (
            <div><dt>VIN</dt><dd className="mono">{charge.vin}</dd></div>)}
          <div><dt>Created</dt><dd>{when(charge.created_at)}</dd></div>
          {paid && (
            <div><dt>Paid</dt><dd>{when(charge.paid_at)}</dd></div>)}
          {paid && (
            <div>
              <dt>Card</dt>
              <dd>
                {charge.card_brand || charge.card_last4
                  ? `${charge.card_brand ?? "Card"}${charge.card_last4 ? ` ending ${charge.card_last4}` : ""}`
                  : "Card"}
              </dd>
            </div>)}
          {charge.refunded_at && (
            <div><dt>Refunded</dt><dd>{when(charge.refunded_at)}</dd></div>)}
        </dl>
      </Panel>

      {open && (
        <Panel title="Collect it" rail="money"
               note={cardPaymentsConfigured() ? undefined : "Cards are not set up"}>
          {!cardPaymentsConfigured() ? (
            <p className="hint">
              VITE_STRIPE_PUBLISHABLE_KEY and VITE_PAYMENTS_WORKER have to be
              set on this site before a card can be taken.
            </p>
          ) : taking ? (
            <ChargeCard
              ref_={{ chargeId: charge.id }}
              amountCents={charge.amount_cents}
              payLabel="Charge"
              onCancel={() => setTaking(false)}
              onPaid={() => { setTaking(false); setNote("Paid. Print the receipt below."); void load(); }} />
          ) : linking ? (
            <>
              <p className="hint">
                The link goes to the customer's inbox and lets them pay from
                their own phone. Paying it retires it, and it expires in a week
                either way. Sending a new one stops the old one working.
              </p>
              <Field label="Send to" value={linkTo} onChange={setLinkTo}
                     type="email" inputMode="email"
                     hint="The customer's email address." />
              <div className="actions">
                <Button disabled={busy === "link"}
                        onClick={() => void run("link", async () => {
                          const sent = await sendPaymentLink(charge, linkTo);
                          setSentUrl(sent.url);
                          setLinking(false);
                          setNote(`Link emailed to ${linkTo.trim().toLowerCase()}.`);
                        })}>
                  {busy === "link" ? "Sending" : "Email the link"}
                </Button>
                <Button variant="ghost" onClick={() => setLinking(false)}
                        disabled={busy === "link"}>Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <div className="actions">
                <Button onClick={() => setTaking(true)}>
                  Take the card now
                </Button>
                <Button variant="ghost" onClick={() => setLinking(true)}>
                  {charge.has_link ? "Send a new link" : "Send a payment link"}
                </Button>
              </div>
              <p className="hint dim">
                Take the card if the customer is standing here. Send a link if
                they are not.
              </p>
            </>
          )}

          {charge.has_link && !linking && !taking && (
            <p className={`hint ${charge.link_expired ? "err" : ""}`}>
              {charge.link_expired
                ? `The link sent to ${charge.link_to_email} expired ${when(charge.link_expires_at)}. Send a new one.`
                : `A link was emailed to ${charge.link_to_email} on ${when(charge.link_sent_at)} and works until ${when(charge.link_expires_at)}.`}
              {" "}
              <button className="linkbtn" disabled={busy === "unlink"}
                      onClick={() => void run("unlink", () => cancelPaymentLink(charge.id))}>
                stop it working
              </button>
            </p>
          )}

          {sentUrl && (
            <p className="hint dim">
              If they would rather have it read out: <span className="mono">{sentUrl}</span>
            </p>
          )}
        </Panel>
      )}

      {paid && (
        <Panel title="Receipt" rail="record">
          <div className="actions">
            <Button onClick={() => void printReceipt()} disabled={busy !== null}>
              {busy === "print" ? "Building" : "Print receipt"}
            </Button>
            <Button variant="ghost" onClick={() => void emailReceipt()}
                    disabled={busy !== null || !(linkTo.trim() || charge.customer_email)}>
              {busy === "email" ? "Sending" : "Email receipt"}
            </Button>
          </div>
          {!charge.customer_email && (
            <>
              <p className="hint">
                This customer has no email address on file. Type one to send
                the receipt, or print it.
              </p>
              <Field label="Send to" value={linkTo} onChange={setLinkTo}
                     type="email" inputMode="email" hint="" />
            </>
          )}
          <p className="hint dim">
            Printing files a copy against this payment, so the receipt can be
            reprinted years later exactly as it went out.
          </p>
        </Panel>
      )}

      <Panel title="History" rail="record" note={`${events.length} entries`}>
        {events.length === 0 ? (
          <p className="hint dim">Nothing recorded yet.</p>
        ) : (
          <ul className="timeline">
            {events.map((e) => (
              <li key={e.id}>
                <span className="dim">{when(e.at)}</span>{" — "}
                {labelForEvent(e.kind)}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {(open || charge.status === "paid") && (
        <Panel title={open ? "Cancel it" : "Send it back"} rail="danger">
          {confirming === "void" ? (
            <Confirm
              message={`Cancel ${charge.charge_no}? Any link already sent stops working.`}
              confirmLabel="Cancel the payment" cancelLabel="Keep it"
              onCancel={() => { setConfirming(null); setReason(""); }}
              onConfirm={() => {
                setConfirming(null);
                void run("void", () => voidCharge(charge.id, reason));
              }}>
              <Field label="Why" value={reason} onChange={setReason}
                     hint="Kept with the payment." />
            </Confirm>
          ) : confirming === "refund" ? (
            <Confirm
              message={`Refund ${formatMoney(charge.amount_cents)} to the `
                     + `${charge.card_brand ?? "card"}${charge.card_last4 ? ` ending ${charge.card_last4}` : ""}? `
                     + "This sends the money back through Stripe and cannot be undone."}
              confirmLabel="Refund it" cancelLabel="Keep it"
              onCancel={() => { setConfirming(null); setReason(""); }}
              onConfirm={() => {
                setConfirming(null);
                void run("refund", () => refund(charge.id, reason));
              }}>
              <Field label="Why" value={reason} onChange={setReason}
                     hint="Goes to Stripe with the refund and stays on the payment." />
            </Confirm>
          ) : (
            <>
              <p className="hint">
                {open
                  ? "Cancelling an unpaid payment leaves the record and stops "
                    + "the link. Nothing has been collected, so there is nothing to send back."
                  : "A refund goes back to the same card in full. FrontLot does "
                    + "not do part refunds — a charge marked refunded has to mean "
                    + "all of it went back."}
              </p>
              <div className="actions">
                <Button variant="danger" disabled={busy !== null}
                        onClick={() => setConfirming(open ? "void" : "refund")}>
                  {open ? "Cancel this payment" : "Refund in full"}
                </Button>
              </div>
            </>
          )}
        </Panel>
      )}

      <div className="actions">
        <Button variant="ghost" onClick={onBack}>Back to payments</Button>
      </div>
    </>
  );
}
